/*******************************************************************************
* @license
* Copyright (c) 2016 - present Accedo. All Rights Reserved.
* 
* This source code, its use, and distribution, is subject to the terms
* and conditions of the applicable license agreement.
* 
* VDK Web ProgramGuide Component (@accedo/vdkweb-epg) 7.2.8
* 
*******************************************************************************/
"use strict";

function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.formatProgramTime = exports["default"] = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _momentMini = _interopRequireDefault(require("moment-mini"));
var _classnames = _interopRequireDefault(require("classnames"));
var _program = _interopRequireDefault(require("./program.scss"));
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { "default": obj }; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var formatProgramTime = function formatProgramTime(startTime, endTime, timeFormat, rtl) {
  var formattedEndTime = endTime.format(timeFormat);
  var formattedStartTime = startTime.format(timeFormat);
  return rtl ? "".concat(formattedEndTime, "-").concat(formattedStartTime) : "".concat(formattedStartTime, "-").concat(formattedEndTime);
};

/**
 * A component for displaying program information.
 * Displays a title and the program's timespan.
 * Time format can be altered by providing a custom
 * time format or time formatter function.
 * Also reacts to clicks if provided with a click handler.
 *
 * @param {object} props See propTypes
 * @returns {Program} A Program component
 */
exports.formatProgramTime = formatProgramTime;
var Program = function Program(_ref) {
  var _classNames;
  var children = _ref.children,
    draggable = _ref.draggable,
    endTime = _ref.endTime,
    isFocused = _ref.isFocused,
    nav = _ref.nav,
    onClick = _ref.onClick,
    ongoing = _ref.ongoing,
    realEndTime = _ref.realEndTime,
    realStartTime = _ref.realStartTime,
    startTime = _ref.startTime,
    style = _ref.style,
    theme = _ref.theme,
    timeFormat = _ref.timeFormat,
    title = _ref.title,
    rtl = _ref.rtl;
  var onMouseEnter = nav.onMouseOver,
    onMouseLeave = nav.onMouseOut;
  var end = (0, _momentMini["default"])(realEndTime || endTime);
  var start = (0, _momentMini["default"])(realStartTime || startTime);
  var displayTime = typeof timeFormat === 'function' ? timeFormat(start, end) : formatProgramTime(start, end, timeFormat, rtl);
  var containerClassName = (0, _classnames["default"])(theme.innerContainer, (_classNames = {}, _defineProperty(_classNames, theme.focused, isFocused), _defineProperty(_classNames, theme.ongoing, ongoing), _defineProperty(_classNames, theme.clickable, !!onClick), _classNames));
  var ref = draggable && _react["default"].useRef(null);
  if (draggable) {
    _react["default"].useEffect(function () {
      ref.current.addEventListener('vdk.epg.programClick', onClick);
      return function () {
        ref.current.removeEventListener('vdk.epg.programClick', onClick);
      };
    }, []);
  }
  return /*#__PURE__*/_react["default"].createElement("div", {
    ref: ref,
    className: theme.program,
    onClick: onClick,
    onMouseEnter: onMouseEnter,
    onMouseLeave: onMouseLeave,
    style: style
  }, /*#__PURE__*/_react["default"].createElement("div", {
    className: containerClassName
  }, children || /*#__PURE__*/_react["default"].createElement("div", null, /*#__PURE__*/_react["default"].createElement("span", {
    className: theme.titleText
  }, title), /*#__PURE__*/_react["default"].createElement("span", {
    className: theme.timeText
  }, "".concat(displayTime)))));
};
Program.defaultProps = {
  nav: {},
  theme: _program["default"],
  timeFormat: 'HH:mm'
};
Program.propTypes = {
  /** React children */
  children: _propTypes["default"].node,
  /** EPG is draggable */
  draggable: _propTypes["default"].bool,
  /** When the program ends (unix timestamp) */
  endTime: _propTypes["default"].number,
  /** Passed by VDK Navigation */
  isFocused: _propTypes["default"].bool,
  /**
   * Navigation object for supporting key navigation and
   * global focus management.
   */
  nav: _propTypes["default"].shape({
    isFocused: _propTypes["default"].bool,
    onMouseOut: _propTypes["default"].func,
    onMouseOver: _propTypes["default"].func
  }),
  /** Click handler, will pass the program object as argument */
  onClick: _propTypes["default"].func,
  /** If the program is ongoing at the moment */
  ongoing: _propTypes["default"].bool,
  /**
   * When passing splitProgramsByTimespan prop to the ProgramGuide, this will
   * be the real (before split) endTime
   */
  realEndTime: _propTypes["default"].number,
  /**
   * When passing splitProgramsByTimespan prop to the ProgramGuide, this will be
   * the real (before split) startTime
   */
  realStartTime: _propTypes["default"].number,
  /** Right to left */
  rtl: _propTypes["default"].bool,
  /** When the program starts (unix timestamp) */
  startTime: _propTypes["default"].number,
  /** Style object to be applied to the outer container div */
  style: _propTypes["default"].object,
  /** Classname API for theming */
  theme: _propTypes["default"].shape({
    clickable: _propTypes["default"].string,
    focused: _propTypes["default"].string,
    innerContainer: _propTypes["default"].string,
    ongoing: _propTypes["default"].string,
    program: _propTypes["default"].string,
    timeText: _propTypes["default"].string,
    title: _propTypes["default"].string,
    titleText: _propTypes["default"].string
  }),
  /**
   * Time format, either a string as supported by moment.js (e.g. 'HH:mm')
   * or a function for performing formatting.
   * The function should have the following interface:
   * ```timeFormat(startTime, endTime, timeFormatString)```
   */
  timeFormat: _propTypes["default"].oneOfType([_propTypes["default"].string, _propTypes["default"].func]),
  /** Program title to be displayed */
  title: _propTypes["default"].string
};
var _default = /*#__PURE__*/_react["default"].memo(Program);
exports["default"] = _default;